import { Dispatch, Fragment, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { AdvancedMarker, APIProvider, Map, Pin, useMap } from "@vis.gl/react-google-maps";
import { Button } from "primereact/button";
import { AddressSearch } from "./AddressSearch";

interface AddressMapProps {
  locationGps: string;
  setLocationGps: Dispatch<string>;
  locationLabel: string;
  setLocationLabel: Dispatch<string>;
  setVisible: Dispatch<boolean>;
  handleChange?: (event: { address: string; location: { lat: number; lng: number } }) => void;
}

// Reverse geocoding component
function ReverseGeocoder({
  position,
  onAddressFound,
}: {
  position: { lat: number; lng: number };
  onAddressFound: (address: string) => void;
}) {
  const map = useMap();
  const geocoder = useRef<google.maps.Geocoder>();

  useEffect(() => {
    if (!map) return;
    geocoder.current = new google.maps.Geocoder();
  }, [map]);

  useEffect(() => {
    if (!geocoder.current || !position) return;

    geocoder.current.geocode({ location: position }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        onAddressFound(results[0].formatted_address);
      }
    });
  }, [position, onAddressFound]);

  return null;
}

// Component to handle map centering
function MapController({ center }: { center: { lat: number; lng: number } }) {
  const map = useMap();

  useEffect(() => {
    if (map && center) {
      map.panTo(center);
    }
  }, [map, center]);

  return null;
}

export function AddressMap({
  locationLabel,
  setLocationLabel,
  setLocationGps,
  locationGps,
  setVisible,
  handleChange,
}: AddressMapProps) {
  const [markerPosition, setMarkerPosition] = useState<{ lat: number; lng: number }>({
    lat: 40.7128,
    lng: -74.006,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string>("");
  const [currentLocation, setCurrentLocation] = useState<{
    location: string;
    address: string;
  }>();
  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
    address: string;
  }>();

  useEffect(() => {
    setCurrentLocation({
      location: locationGps,
      address: locationLabel,
    });
  }, []);

  // Request user's current location on component mount
  useEffect(() => {
    const getCurrentLocation = () => {
      if (!navigator.geolocation) {
        setLocationError("Geolocation is not supported by this browser.");
        return;
      }

      setIsLoadingLocation(true);
      navigator.geolocation.getCurrentPosition(
        position => {
          const { latitude, longitude } = position.coords;
          const newPosition = { lat: latitude, lng: longitude };
          setMarkerPosition(newPosition);
          setLocationGps(`${latitude},${longitude}`);
          setIsLoadingLocation(false);
        },
        error => {
          console.error("Error getting location:", error);
          setLocationError("Unable to retrieve your location. Using default location.");
          setIsLoadingLocation(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    };

    // Only get current location if no existing location is provided AND no location label exists
    if (!locationGps && !locationLabel) {
      getCurrentLocation();
    }
  }, []);

  // Initialize marker position from props if available
  useEffect(() => {
    if (locationGps) {
      try {
        const [lat, lng] = locationGps.split(",").map(Number);
        if (!isNaN(lat) && !isNaN(lng)) {
          setMarkerPosition({ lat, lng });
        }
      } catch (error) {
        console.error("Error parsing locationGps:", error);
      }
    }
  }, [locationGps]);

  const handleLocationSelect = (location: { lat: number; lng: number; address: string }) => {
    setMarkerPosition(location);
    setLocationGps(`${location.lat},${location.lng}`);
    // setLocationLabel(location.address);
  };

  const handleMarkerDragEnd = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const newPosition = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
      setSelectedLocation(prev => ({
        address: prev?.address || "",
        ...newPosition,
      }));
      setMarkerPosition(newPosition);
      setLocationGps(`${newPosition.lat},${newPosition.lng}`);
      setIsDragging(false);
    }
  };

  const handleAddressFound = (address: string) => {
    if (!isDragging) {
      setLocationLabel(address);
      setSelectedLocation(prev => ({
        address: address,
        lat: prev?.lat ?? markerPosition.lat,
        lng: prev?.lng ?? markerPosition.lng,
      }));
    }
  };

  const handleConfirm = () => {
    setVisible(false);
    handleChange?.({
      address: selectedLocation?.address ?? "",
      location: {
        lat: selectedLocation?.lat ?? markerPosition.lat,
        lng: selectedLocation?.lng ?? markerPosition.lng,
      },
    });
  };

  const handleCancel = () => {
    setLocationGps(currentLocation?.location || "");
    setLocationLabel(currentLocation?.address || "");
    setTimeout(() => {
      setVisible(false);
    }, 1000);
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by this browser.");
      return;
    }

    setIsLoadingLocation(true);
    setLocationError("");

    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude } = position.coords;
        const newPosition = { lat: latitude, lng: longitude };
        setMarkerPosition(newPosition);
        setLocationGps(`${latitude},${longitude}`);
        setIsLoadingLocation(false);
      },
      error => {
        console.error("Error getting location:", error);
        setLocationError("Unable to retrieve your location. Please try again or search manually.");
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000, // 1 minute
      }
    );
  };

  return (
    <Fragment>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "8px",
            width: "90%",
            maxWidth: "800px",
            height: "80%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ marginBottom: "10px" }}>
            <h3 style={{ margin: "0 0 10px 0" }}>Select Location</h3>
            {locationError && (
              <div
                style={{
                  color: "#d32f2f",
                  fontSize: "12px",
                  marginBottom: "10px",
                }}
              >
                {locationError}
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "end",
              gap: "10px",
              marginBottom: "10px",
            }}
          >
            <AddressSearch initialValue={locationLabel} onLocationSelect={handleLocationSelect} />
            <Button
              icon="pi pi-map-marker"
              onClick={handleGetCurrentLocation}
              disabled={isLoadingLocation}
              style={{ whiteSpace: "nowrap", color: "grey", height: "50px", marginBottom: "16px" }}
              outlined
              size="small"
            />
          </div>

          <div style={{ flex: 1, minHeight: "350px", marginBottom: "10px" }}>
            <Map
              defaultZoom={13}
              center={markerPosition}
              mapId="location-picker-map"
              style={{ width: "100%", height: "100%" }}
            >
              <MapController center={markerPosition} />
              <AdvancedMarker
                position={markerPosition}
                draggable={true}
                onDragStart={() => setIsDragging(true)}
                onDragEnd={handleMarkerDragEnd}
              >
                <Pin background={"red"} glyphColor={"#000"} borderColor={"#000"} />
              </AdvancedMarker>

              <ReverseGeocoder position={markerPosition} onAddressFound={handleAddressFound} />
            </Map>
          </div>

          <div
            style={{
              padding: "10px",
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
              marginBottom: "10px",
              fontSize: "14px",
            }}
          >
            <strong>Selected Address:</strong> {locationLabel || "Loading address..."}
          </div>

          <div
            style={{
              display: "flex",
              gap: "10px",
              justifyContent: "flex-end",
            }}
          >
            <Button label="Cancel" severity="secondary" onClick={handleCancel} />
            <Button label="Confirm Location" onClick={handleConfirm} />
          </div>
        </div>
      </div>
    </Fragment>
  );
}
